import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { fetchIceServers } from './turn-credentials';
import { parseSignalingMessage, type SignalingMessage } from './signaling';
import { aggregateConnectionState, isPolite, shouldAttemptIceRestart } from './negotiation';
import { CAMERA_VIDEO_CONSTRAINTS, SCREEN_SHARE_VIDEO_CONSTRAINTS } from './media-quality';
import { computeSendBudget } from './bitrate-budget';
import type {
  VideoService,
  VisioConnectionState,
  VisioLocalMedia,
  VisioParticipant,
  VisioRole,
} from './video-service';

interface PresenceMeta {
  userId: string;
  role: VisioRole;
  displayName: string;
  /** Absent chez un ancien client (qui envoyait toujours micro + caméra) → traité comme ouvert. */
  micOn?: boolean;
  cameraOn?: boolean;
}

type PeerIdentity = Pick<PresenceMeta, 'userId' | 'role' | 'displayName'>;

interface PeerState {
  pc: RTCPeerConnection;
  meta: PresenceMeta;
  /** Flux DISTANT de ce pair, construit ici (on y ajoute chaque piste reçue) — indépendant de `event.streams`. */
  stream: MediaStream | null;
  /** Émetteurs des transceivers audio/vidéo, TOUJOURS négociés (piste éventuellement nulle) :
   * allumer la caméra/le micro plus tard = `replaceTrack`, sans renégociation. */
  audioSender: RTCRtpSender | null;
  videoSender: RTCRtpSender | null;
  makingOffer: boolean;
  ignoreOffer: boolean;
  iceRestartAttempts: number;
  screenSharing: boolean;
  /** Candidats ICE reçus AVANT que `setRemoteDescription` ait abouti — `addIceCandidate`
   * lève `InvalidStateError` tant que `pc.remoteDescription` est `null` (l'offre/réponse
   * et les candidats arrivent en messages séparés sur le même canal, rien ne garantit
   * que le traitement de l'offre se termine avant que les premiers candidats arrivent).
   * Vidée dans `flushPendingCandidates` juste après chaque `setRemoteDescription` réussi. */
  pendingCandidates: RTCIceCandidateInit[];
  /** File de traitement des signaux de négociation de CE pair : ils sont appliqués UN PAR UN,
   * dans l'ordre d'arrivée. Sans ça, une offre qui arrive pendant que la réponse précédente est
   * encore en cours d'application est jugée « en collision » et jetée pour toujours (mesuré :
   * 10 à 16 connexions bloquées sur 56 dans une salle de 8). */
  queue: Promise<void>;
  /** Renvoi de l'offre en attente si aucune réponse n'arrive (message Realtime perdu). */
  offerTimer: ReturnType<typeof setTimeout> | null;
}

export interface WebRtcVideoServiceOptions {
  /**
   * Id de l'HÔTE de la salle (le tuteur), tel que le serveur le connaît. C'est LUI qu'on
   * croit pour « fin de réunion », « coupe ton micro »… — jamais le rôle annoncé en
   * présence, qu'un participant règle lui-même. Absent : repli sur le rôle `tutor` annoncé
   * (comportement historique du 1:1).
   */
  hostUserId?: string;
}

/** Délais de vérification d'une fin de réunion annoncée (ms) : 1ʳᵉ tentative immédiate, puis une reprise. */
const ROOM_ENDED_VERIFY_DELAYS_MS = [0, 1500];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Une offre sans réponse est renvoyée après ce délai (×tentative), au plus `OFFER_RESEND_MAX` fois. */
const OFFER_RESEND_MS = 4000;
const OFFER_RESEND_MAX = 3;

/**
 * WebRTC auto-hébergé — signalisation via Supabase Realtime (canal privé
 * `visio-room:{roomId}`, autorisé par RLS sur `realtime.messages`, migrations 0236/0257).
 * Pattern "perfect negotiation" (pas d'initiateur élu — les pairs réagissent à
 * `onnegotiationneeded`, les collisions se résolvent via poli/impoli, cf. `negotiation.ts`).
 *
 * MAILLAGE à N pairs : `peers` a une `RTCPeerConnection` par participant. Chacun envoie sa
 * vidéo à chacun des autres → le débit montant est réparti par `computeSendBudget`
 * (au-delà de ~8 participants il faut un SFU : `transport = 'sfu'`, cf. `factory.ts`).
 *
 * Caméra et micro sont OPTIONNELS et acquis À LA DEMANDE : chaque pair a toujours un
 * transceiver audio ET vidéo `sendrecv` (piste éventuellement nulle), donc un participant
 * sans caméra reçoit quand même les autres, et allumer la sienne plus tard n'exige aucune
 * renégociation (`replaceTrack`).
 *
 * `db`/`supabaseUrl` sont INJECTÉS (pas de singleton `@/lib/supabase`
 * importé) : ce service est partagé entre `apps/tutor` (salle web,
 * tutor.jeraisonne.fr) et la route native de `apps/app` — chaque app a son
 * propre client Supabase, connecté au même projet.
 */
export class WebRtcVideoService implements VideoService {
  private channel: RealtimeChannel | null = null;
  private roomId: string | null = null;
  private localStream: MediaStream | null = null;
  private iceServers: RTCIceServer[] = [];
  private peers = new Map<string, PeerState>();
  private participantsListeners = new Set<(p: VisioParticipant[]) => void>();
  private connectionListeners = new Set<(s: VisioConnectionState) => void>();
  private roomEndedListeners = new Set<() => void>();
  private screenShareListeners = new Set<(sharing: boolean) => void>();
  private transcriptionListeners = new Set<(active: boolean) => void>();
  private muteRequestListeners = new Set<() => void>();
  private excludedListeners = new Set<() => void>();
  private presence: PresenceMeta;
  private readonly hostUserId: string | null;
  /** Pistes LOCALES : micro et caméra (indépendantes du partage d'écran, qui a sa propre piste). */
  private micTrack: MediaStreamTrack | null = null;
  private cameraTrack: MediaStreamTrack | null = null;
  private screenTrack: MediaStreamTrack | null = null;
  private acquiring = new Set<'audio' | 'video'>();
  private lastConnectionState: VisioConnectionState | null = null;
  private roomEndedEmitted = false;
  private budgetTimer: ReturnType<typeof setTimeout> | null = null;
  /** Dernier état envoyé — resynchronisé vers tout pair qui rejoint/rejoint à nouveau APRÈS ce point. */
  private lastSentTranscriptionActive: boolean | null = null;
  private lastSentScreenSharing = false;

  constructor(
    private readonly me: PeerIdentity,
    private readonly db: SupabaseClient,
    private readonly supabaseUrl: string,
    opts: WebRtcVideoServiceOptions = {},
  ) {
    this.hostUserId = opts.hostUserId ?? null;
    this.presence = { ...me };
  }

  /** L'hôte de confiance (id serveur) ; repli sur le rôle annoncé quand l'id n'est pas fourni. */
  private isHostId(userId: string): boolean {
    if (this.hostUserId) return userId === this.hostUserId;
    const role = userId === this.me.userId ? this.me.role : this.peers.get(userId)?.meta.role;
    return role === 'tutor';
  }

  private isLive(track: MediaStreamTrack | null): track is MediaStreamTrack {
    return !!track && track.readyState !== 'ended';
  }

  getLocalMedia(): VisioLocalMedia {
    return {
      micOn: this.isLive(this.micTrack) && this.micTrack.enabled,
      cameraOn: this.isLive(this.cameraTrack) && this.cameraTrack.enabled,
    };
  }

  async joinRoom(roomId: string, opts: { localStream: MediaStream }): Promise<void> {
    this.roomId = roomId;
    this.localStream = opts.localStream;
    this.roomEndedEmitted = false;
    this.micTrack = opts.localStream.getAudioTracks()[0] ?? null;
    this.cameraTrack = opts.localStream.getVideoTracks()[0] ?? null;
    this.watchEnded(this.micTrack, 'audio');
    this.watchEnded(this.cameraTrack, 'video');
    this.presence = { ...this.presence, ...this.getLocalMedia() };

    this.iceServers = await fetchIceServers(roomId, this.db, this.supabaseUrl);
    console.log(
      `[webrtc] iceServers résolus: ${this.iceServers.map((s) => (Array.isArray(s.urls) ? s.urls.join('|') : s.urls)).join(', ')}`,
    );
    this.emitConnectionState('connecting');

    const channel = this.db.channel(`visio-room:${roomId}`, {
      config: { private: true, presence: { key: this.me.userId } },
    });
    this.channel = channel;

    channel.on('presence', { event: 'sync' }, () => this.handlePresenceSync());
    channel.on('presence', { event: 'leave' }, ({ key }: { key: string }) => {
      // Une MISE À JOUR de présence (micro/caméra → `track`) émet un `leave` de l'ANCIENNE
      // entrée alors que la nouvelle est déjà là : ce n'est PAS un départ.
      if ((this.channel?.presenceState()[key]?.length ?? 0) > 0) return;
      this.handlePeerLeft(key);
    });
    channel.on('broadcast', { event: 'signal' }, ({ payload }: { payload: unknown }) => {
      void this.handleSignal(payload);
    });

    await new Promise<void>((resolve, reject) => {
      channel.subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          void channel.track(this.presence).then(() => resolve());
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('[webrtc] realtime subscribe failed', status, err);
          reject(new Error(`realtime subscribe failed: ${status}${err ? `: ${err.message}` : ''}`));
        }
      });
    });
  }

  private handlePresenceSync(): void {
    if (!this.channel) return;
    const state = this.channel.presenceState<PresenceMeta>();

    for (const key of Object.keys(state)) {
      if (key === this.me.userId) continue;
      const metas = state[key];
      const meta = metas?.[metas.length - 1];
      if (!meta) continue;

      const existing = this.peers.get(meta.userId);
      if (!existing) {
        this.createPeer(meta);
      } else {
        // La présence est la source des métadonnées (nom, rôle, micro/caméra) : elle complète
        // aussi un pair créé défensivement depuis un signal arrivé avant ce sync (handleSignal).
        existing.meta = meta;
      }
    }
    this.emitParticipants();
  }

  private handlePeerLeft(userId: string): void {
    const peer = this.peers.get(userId);
    if (!peer) return;
    this.closePeer(peer);
    this.peers.delete(userId);
    this.emitParticipants();
    this.recomputeConnectionState();
    this.scheduleSendBudget();
  }

  /** Piste vidéo actuellement ENVOYÉE : le partage d'écran prime sur la caméra. */
  private outgoingVideoTrack(): MediaStreamTrack | null {
    if (this.screenTrack) return this.screenTrack;
    return this.isLive(this.cameraTrack) ? this.cameraTrack : null;
  }

  private createPeer(meta: PresenceMeta): PeerState {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const peer: PeerState = {
      pc,
      meta,
      stream: null,
      audioSender: null,
      videoSender: null,
      makingOffer: false,
      ignoreOffer: false,
      iceRestartAttempts: 0,
      screenSharing: false,
      pendingCandidates: [],
      queue: Promise.resolve(),
      offerTimer: null,
    };
    this.peers.set(meta.userId, peer);

    // Resynchronise l'état courant vers un pair qui vient d'apparaître (ou
    // réapparaît après une reconnexion) — sans ça, un partage d'écran déjà en
    // cours affiche `screenSharing:false` par défaut côté nouveau pair, et le
    // tuteur devrait retoggler la retranscription pour qu'elle atteigne un
    // élève qui rejoint après coup.
    if (this.lastSentScreenSharing) {
      this.sendSignal({
        kind: 'screen-share-state',
        from: this.me.userId,
        to: meta.userId,
        data: { sharing: true },
      });
    }
    if (this.isHostId(this.me.userId) && this.lastSentTranscriptionActive !== null) {
      this.sendSignal({
        kind: 'transcription-state',
        from: this.me.userId,
        to: meta.userId,
        data: { active: this.lastSentTranscriptionActive },
      });
    }

    // UN SEUL offreur par paire : le pair « impoli » (id le plus grand). Si les deux côtés
    // créaient leurs transceivers et offraient en même temps (collision à CHAQUE entrée),
    // le « poli » devrait annuler (rollback) son offre puis renégocier — et Chromium ne
    // réutilise PAS pour l'offre reçue les transceivers créés par `addTransceiver` : chaque
    // paire finissait avec 4 m-lignes au lieu de 2, deux tours de négociation, et un
    // rollback fragile (surtout Safari). Ici l'offreur crée un transceiver audio ET vidéo
    // `sendrecv` (piste éventuellement nulle : un participant sans caméra reçoit quand même
    // les autres, et allumer la sienne = `replaceTrack`, sans renégociation) ; l'autre côté
    // attend l'offre et y rattache ses propres pistes (`bindLocalMedia`).
    if (!isPolite(this.me.userId, meta.userId)) {
      const streams = this.localStream ? [this.localStream] : [];
      const audioTx = pc.addTransceiver(this.isLive(this.micTrack) ? this.micTrack : 'audio', {
        direction: 'sendrecv',
        streams,
      });
      const videoTrack = this.outgoingVideoTrack();
      const videoTx = pc.addTransceiver(videoTrack ?? 'video', { direction: 'sendrecv', streams });
      peer.audioSender = audioTx.sender;
      peer.videoSender = videoTx.sender;
      this.scheduleSendBudget();
    }

    pc.ontrack = (event) => {
      console.log(`[webrtc] ontrack from ${meta.userId}: kind=${event.track.kind}`);
      if (!peer.stream) peer.stream = new MediaStream();
      if (!peer.stream.getTracks().includes(event.track)) peer.stream.addTrack(event.track);
      this.emitParticipants();
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignal({
          kind: 'ice-candidate',
          from: this.me.userId,
          to: meta.userId,
          data: event.candidate.toJSON(),
        });
      }
    };

    pc.onicecandidateerror = (event) => {
      const e = event as RTCPeerConnectionIceErrorEvent;
      console.error(
        `[webrtc] icecandidateerror from ${meta.userId}: code=${e.errorCode} text=${e.errorText} url=${e.url}`,
      );
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[webrtc] iceConnectionState with ${meta.userId}: ${pc.iceConnectionState}`);
    };

    // Se déclenche pour les DEUX pairs dès qu'un transceiver est ajouté — pas
    // d'initiateur élu, les collisions se résolvent dans handleSignal (offer).
    pc.onnegotiationneeded = () => {
      void (async () => {
        try {
          peer.makingOffer = true;
          await pc.setLocalDescription();
          this.sendSignal({
            kind: 'offer',
            from: this.me.userId,
            to: meta.userId,
            data: { type: pc.localDescription!.type, sdp: pc.localDescription!.sdp },
          });
          this.armOfferWatchdog(peer, 1);
        } catch (err) {
          console.error('[webrtc] negotiationneeded failed', err);
        } finally {
          peer.makingOffer = false;
        }
      })();
    };

    pc.onconnectionstatechange = () => {
      console.log(`[webrtc] connectionState with ${meta.userId}: ${pc.connectionState}`);
      if (pc.connectionState === 'connected') {
        // Une coupure passagère ne doit pas épuiser définitivement les tentatives de reprise.
        peer.iceRestartAttempts = 0;
        this.scheduleSendBudget();
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.attemptIceRestart(peer);
      }
      this.emitParticipants();
      this.recomputeConnectionState();
    };

    return peer;
  }

  private attemptIceRestart(peer: PeerState): void {
    peer.iceRestartAttempts += 1;
    if (!shouldAttemptIceRestart(peer.iceRestartAttempts)) return; // épuisé : l'agrégat le verra
    // `restartIce()` déclenche `onnegotiationneeded` (spec) — réutilise le
    // chemin d'offre normal, pas de duplication de logique de négociation.
    peer.pc.restartIce();
  }

  private recomputeConnectionState(): void {
    const next = aggregateConnectionState(
      [...this.peers.values()].map((p) => ({
        connectionState: p.pc.connectionState,
        restartsExhausted: !shouldAttemptIceRestart(p.iceRestartAttempts),
      })),
    );
    if (next && next !== this.lastConnectionState) this.emitConnectionState(next);
  }

  private async handleSignal(raw: unknown): Promise<void> {
    const msg = parseSignalingMessage(raw);
    if (!msg) return;

    // Diffusé à TOUS (aucun destinataire) : traité avant le filtre `to`.
    if (msg.kind === 'room-ended') {
      await this.confirmRoomEnded();
      return;
    }

    if (msg.to !== this.me.userId) return;

    if (msg.kind === 'bye') {
      // LEGACY : un ancien client hôte l'envoyait pour dire « fin de réunion ». Un `bye` ne
      // signifie plus que « ce pair part » ; s'il vient de l'HÔTE on vérifie côté serveur si
      // la réunion est réellement terminée (l'hôte peut aussi être simplement parti).
      const fromHost = this.isHostId(msg.from);
      this.handlePeerLeft(msg.from);
      if (fromHost) await this.confirmRoomEnded();
      return;
    }

    if (msg.kind === 'mute-request') {
      // Seul l'hôte peut demander de couper un micro ; on coupe, jamais on ne rouvre.
      if (!this.isHostId(msg.from)) return;
      await this.setMicEnabled(false);
      for (const cb of this.muteRequestListeners) cb();
      return;
    }

    if (msg.kind === 'excluded') {
      // Seul l'hôte peut exclure ; on quitte immédiatement (la salle continue pour les autres).
      if (!this.isHostId(msg.from)) return;
      await this.leaveRoom();
      for (const cb of this.excludedListeners) cb();
      return;
    }

    let peer = this.peers.get(msg.from);
    if (!peer) {
      // Signal ciblé reçu avant notre propre sync de présence pour ce pair
      // (ordre réseau) — on le crée avec des métadonnées provisoires,
      // complétées au prochain sync (cf. handlePresenceSync).
      peer = this.createPeer({ userId: msg.from, role: 'student', displayName: '' });
    }

    if (msg.kind === 'screen-share-state') {
      peer.screenSharing = msg.data.sharing;
      this.emitParticipants();
    } else if (msg.kind === 'transcription-state') {
      for (const cb of this.transcriptionListeners) cb(msg.data.active);
    } else {
      // offre / réponse / candidat ICE : appliqués UN PAR UN, dans l'ordre (cf. PeerState.queue).
      await this.enqueue(peer, () => this.processNegotiationSignal(peer!, msg));
    }
  }

  private enqueue(peer: PeerState, task: () => Promise<void>): Promise<void> {
    peer.queue = peer.queue.then(task).catch((err: unknown) => {
      console.error('[webrtc] traitement d’un signal échoué', err);
    });
    return peer.queue;
  }

  private async processNegotiationSignal(
    peer: PeerState,
    msg: Extract<SignalingMessage, { kind: 'offer' | 'answer' | 'ice-candidate' }>,
  ): Promise<void> {
    const { pc } = peer;
    if (pc.signalingState === 'closed') return; // le pair est parti pendant l'attente
    const polite = isPolite(this.me.userId, msg.from);

    if (msg.kind === 'offer') {
      const offerCollision = peer.makingOffer || pc.signalingState !== 'stable';
      peer.ignoreOffer = !polite && offerCollision;
      if (peer.ignoreOffer) return;

      await pc.setRemoteDescription(msg.data);
      await this.flushPendingCandidates(peer);
      // Le côté qui n'offre pas attache ses pistes aux transceivers créés par l'offre.
      await this.bindLocalMedia(peer);
      await pc.setLocalDescription();
      this.sendSignal({
        kind: 'answer',
        from: this.me.userId,
        to: msg.from,
        data: { type: pc.localDescription!.type, sdp: pc.localDescription!.sdp },
      });
      // L'offreur n'avait pas de piste d'un type (ancien client sans caméra) : on ajoute
      // l'émetteur manquant — cela déclenche notre propre offre.
      this.ensureSenders(peer);
    } else if (msg.kind === 'answer') {
      await pc.setRemoteDescription(msg.data);
      this.clearOfferTimer(peer);
      await this.flushPendingCandidates(peer);
    } else {
      if (!pc.remoteDescription) {
        // La description distante n'est pas encore posée (offre/réponse pas
        // encore traitée) — `addIceCandidate` lèverait `InvalidStateError`.
        // On met en attente, `flushPendingCandidates` les rejouera juste
        // après le prochain `setRemoteDescription` réussi.
        peer.pendingCandidates.push(msg.data);
        return;
      }
      try {
        await pc.addIceCandidate(msg.data);
      } catch (err) {
        if (!peer.ignoreOffer) console.error('[webrtc] addIceCandidate failed', err);
      }
    }
  }

  /**
   * Côté QUI N'OFFRE PAS : après avoir appliqué l'offre reçue, ses transceivers audio/vidéo
   * sont en `recvonly` — on les passe en `sendrecv` et on y met NOS pistes courantes (ou
   * `null`), avant de créer la réponse. Les émetteurs mémorisés servent ensuite à
   * `replaceTrack` (caméra/micro à la demande) et au budget de débit.
   */
  private async bindLocalMedia(peer: PeerState): Promise<void> {
    const streams = this.localStream ? [this.localStream] : [];
    for (const tx of peer.pc.getTransceivers()) {
      const kind = tx.receiver.track.kind;
      if (kind === 'audio' && peer.audioSender === null) {
        peer.audioSender = tx.sender;
        tx.direction = 'sendrecv';
        await tx.sender.replaceTrack(this.isLive(this.micTrack) ? this.micTrack : null);
        tx.sender.setStreams?.(...streams);
      } else if (kind === 'video' && peer.videoSender === null) {
        peer.videoSender = tx.sender;
        tx.direction = 'sendrecv';
        await tx.sender.replaceTrack(this.outgoingVideoTrack());
        tx.sender.setStreams?.(...streams);
      }
    }
    this.scheduleSendBudget();
  }

  /** Ajoute l'émetteur d'un type que l'offre reçue ne contenait pas (déclenche `onnegotiationneeded`). */
  private ensureSenders(peer: PeerState): void {
    const streams = this.localStream ? [this.localStream] : [];
    if (peer.audioSender === null) {
      const tx = peer.pc.addTransceiver(this.isLive(this.micTrack) ? this.micTrack : 'audio', {
        direction: 'sendrecv',
        streams,
      });
      peer.audioSender = tx.sender;
    }
    if (peer.videoSender === null) {
      const tx = peer.pc.addTransceiver(this.outgoingVideoTrack() ?? 'video', {
        direction: 'sendrecv',
        streams,
      });
      peer.videoSender = tx.sender;
      this.scheduleSendBudget();
    }
  }

  /**
   * Realtime Broadcast est « au mieux » : un message peut se perdre (quota dépassé, coupure
   * brève). Une offre restée sans réponse est donc RENVOYÉE (le récepteur, si elle est déjà
   * passée, la traite sans dommage : l'état stable l'accepte de nouveau).
   */
  private armOfferWatchdog(peer: PeerState, attempt: number): void {
    this.clearOfferTimer(peer);
    peer.offerTimer = setTimeout(() => {
      peer.offerTimer = null;
      const { pc } = peer;
      if (pc.signalingState !== 'have-local-offer' || !pc.localDescription) return;
      if (attempt > OFFER_RESEND_MAX) return;
      console.warn(`[webrtc] offre sans réponse vers ${peer.meta.userId} — renvoi ${attempt}`);
      this.sendSignal({
        kind: 'offer',
        from: this.me.userId,
        to: peer.meta.userId,
        data: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
      });
      this.armOfferWatchdog(peer, attempt + 1);
    }, OFFER_RESEND_MS * attempt);
  }

  private clearOfferTimer(peer: PeerState): void {
    if (peer.offerTimer) clearTimeout(peer.offerTimer);
    peer.offerTimer = null;
  }

  private closePeer(peer: PeerState): void {
    this.clearOfferTimer(peer);
    peer.pc.close();
  }

  /**
   * Une fin de réunion annoncée par signal peut être FORGÉE par n'importe quel membre du
   * canal (le canal authentifie l'appartenance, pas l'expéditeur) : on ne l'affiche qu'une
   * fois `video_rooms.ended_at` confirmé par le serveur — seul juge. Sans confirmation
   * (signal usurpé, réseau) on ne fait rien : la vraie fin sera refusée à la reconnexion
   * (`room_ended`).
   */
  private async confirmRoomEnded(): Promise<void> {
    if (this.roomEndedEmitted || !this.roomId) return;
    const roomId = this.roomId;
    for (const delay of ROOM_ENDED_VERIFY_DELAYS_MS) {
      if (delay > 0) await wait(delay);
      if (this.roomEndedEmitted || this.roomId !== roomId) return;
      try {
        const { data, error } = await this.db
          .from('video_rooms')
          .select('ended_at')
          .eq('id', roomId)
          .maybeSingle();
        // Re-testé APRÈS la réponse : `room-ended` et le `bye` d'un ancien client hôte arrivent
        // presque ensemble, deux vérifications tournent alors en parallèle — une seule notifie.
        if (this.roomEndedEmitted) return;
        if (!error && (data as { ended_at: string | null } | null)?.ended_at) {
          this.roomEndedEmitted = true;
          for (const cb of this.roomEndedListeners) cb();
          return;
        }
      } catch (err) {
        console.warn('[webrtc] vérification de fin de réunion échouée', err);
      }
    }
  }

  /** Rejoue les candidats ICE reçus avant que `setRemoteDescription` n'aboutisse — cf. `PeerState.pendingCandidates`. */
  private async flushPendingCandidates(peer: PeerState): Promise<void> {
    if (peer.pendingCandidates.length === 0) return;
    const candidates = peer.pendingCandidates;
    peer.pendingCandidates = [];
    console.log(`[webrtc] flush de ${candidates.length} candidat(s) ICE en attente`);
    for (const candidate of candidates) {
      try {
        await peer.pc.addIceCandidate(candidate);
      } catch (err) {
        console.error('[webrtc] flushed addIceCandidate failed', err);
      }
    }
  }

  private sendSignal(message: SignalingMessage): void {
    void this.channel?.send({ type: 'broadcast', event: 'signal', payload: message });
  }

  private emitParticipants(): void {
    const list: VisioParticipant[] = [...this.peers.values()].map((p) => ({
      userId: p.meta.userId,
      role: p.meta.role,
      displayName: p.meta.displayName,
      stream: p.stream,
      connectionState: p.pc.connectionState,
      screenSharing: p.screenSharing,
      micOn: p.meta.micOn ?? true,
      cameraOn: p.meta.cameraOn ?? true,
      isHost: this.isHostId(p.meta.userId),
    }));
    for (const cb of this.participantsListeners) cb(list);
  }

  private emitConnectionState(state: VisioConnectionState): void {
    this.lastConnectionState = state;
    for (const cb of this.connectionListeners) cb(state);
  }

  onParticipantsChanged(cb: (participants: VisioParticipant[]) => void): () => void {
    this.participantsListeners.add(cb);
    return () => this.participantsListeners.delete(cb);
  }

  onConnectionStateChanged(cb: (state: VisioConnectionState) => void): () => void {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  onRoomEnded(cb: () => void): () => void {
    this.roomEndedListeners.add(cb);
    return () => this.roomEndedListeners.delete(cb);
  }

  onMuteRequested(cb: () => void): () => void {
    this.muteRequestListeners.add(cb);
    return () => this.muteRequestListeners.delete(cb);
  }

  /**
   * Hôte : exclut immédiatement (ferme SA vue de la connexion, envoie le signal). N'écrit rien
   * en base — `remove_visio_participant` (RPC, appelée par l'appelant) est ce qui empêche l'exclu
   * de revenir ; ce signal ne fait que le faire sortir maintenant plutôt qu'à sa prochaine tentative
   * de reconnexion.
   */
  removeParticipant(userId: string): void {
    if (!this.isHostId(this.me.userId) || userId === this.me.userId) return;
    this.sendSignal({ kind: 'excluded', from: this.me.userId, to: userId });
    this.handlePeerLeft(userId);
  }

  onExcluded(cb: () => void): () => void {
    this.excludedListeners.add(cb);
    return () => this.excludedListeners.delete(cb);
  }

  requestMute(userId: string): void {
    if (!this.isHostId(this.me.userId) || userId === this.me.userId) return;
    this.sendSignal({ kind: 'mute-request', from: this.me.userId, to: userId });
  }

  /** Publie l'état micro/caméra dans la présence (les tuiles des autres l'affichent). */
  private updatePresence(): void {
    this.presence = { ...this.presence, ...this.getLocalMedia() };
    if (!this.channel) return;
    void Promise.resolve(this.channel.track(this.presence)).catch((err: unknown) => {
      console.warn('[webrtc] mise à jour de présence échouée', err);
    });
  }

  /** Une piste qui s'arrête d'elle-même (périphérique débranché, permission retirée) = éteinte. */
  private watchEnded(track: MediaStreamTrack | null, kind: 'audio' | 'video'): void {
    if (!track) return;
    track.onended = () => {
      if (kind === 'audio' && this.micTrack === track) {
        this.micTrack = null;
        this.localStream?.removeTrack(track);
        void this.replaceOnAllPeers('audio', null);
      } else if (kind === 'video' && this.cameraTrack === track) {
        this.cameraTrack = null;
        this.localStream?.removeTrack(track);
        if (!this.screenTrack) void this.replaceOnAllPeers('video', null);
      }
      this.updatePresence();
    };
  }

  private async replaceOnAllPeers(
    kind: 'audio' | 'video',
    track: MediaStreamTrack | null,
  ): Promise<void> {
    for (const peer of this.peers.values()) {
      const sender = kind === 'audio' ? peer.audioSender : peer.videoSender;
      if (!sender) continue;
      try {
        await sender.replaceTrack(track);
      } catch (err) {
        console.error(`[webrtc] replaceTrack(${kind}) failed`, err);
      }
    }
    if (kind === 'video') this.scheduleSendBudget();
  }

  private async acquire(kind: 'audio' | 'video'): Promise<MediaStreamTrack | null> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        kind === 'audio' ? { audio: true } : { video: CAMERA_VIDEO_CONSTRAINTS },
      );
      return (kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks())[0] ?? null;
    } catch (err) {
      console.warn(`[webrtc] getUserMedia(${kind}) refusé/indisponible`, err);
      return null;
    }
  }

  async setMicEnabled(enabled: boolean): Promise<boolean> {
    if (!this.localStream) return false;
    if (!enabled) {
      // Couper = désactiver la piste (rouvrir instantanément, sans nouvelle permission).
      if (this.micTrack) this.micTrack.enabled = false;
    } else if (this.isLive(this.micTrack)) {
      this.micTrack.enabled = true;
    } else if (!this.acquiring.has('audio')) {
      this.acquiring.add('audio');
      try {
        const track = await this.acquire('audio');
        if (track) {
          if (!this.localStream) {
            track.stop(); // on a quitté la salle pendant la demande de permission
            return false;
          }
          this.micTrack = track;
          this.watchEnded(track, 'audio');
          this.localStream.addTrack(track);
          await this.replaceOnAllPeers('audio', track);
        }
      } finally {
        this.acquiring.delete('audio');
      }
    }
    this.updatePresence();
    return this.getLocalMedia().micOn;
  }

  async setCameraEnabled(enabled: boolean): Promise<boolean> {
    if (!this.localStream) return false;
    if (!enabled) {
      const camera = this.cameraTrack;
      if (camera) {
        // Éteindre = ARRÊTER la piste (voyant de la caméra éteint, plus d'encodage, plus de débit).
        camera.stop();
        this.cameraTrack = null;
        if (this.localStream.getVideoTracks().includes(camera))
          this.localStream.removeTrack(camera);
        if (!this.screenTrack) await this.replaceOnAllPeers('video', null);
      }
    } else if (this.isLive(this.cameraTrack)) {
      this.cameraTrack.enabled = true;
    } else if (!this.acquiring.has('video')) {
      this.acquiring.add('video');
      try {
        const track = await this.acquire('video');
        if (track) {
          if (!this.localStream) {
            track.stop();
            return false;
          }
          this.cameraTrack = track;
          this.watchEnded(track, 'video');
          // Pendant un partage d'écran, la caméra est gardée en réserve (l'écran prime) :
          // elle repartira à l'arrêt du partage.
          if (!this.screenTrack) {
            this.localStream.addTrack(track);
            await this.replaceOnAllPeers('video', track);
          }
        }
      } finally {
        this.acquiring.delete('video');
      }
    }
    this.updatePresence();
    return this.getLocalMedia().cameraOn;
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Répartit le débit montant entre les pairs (`computeSendBudget`) — appelé à chaque
   * arrivée/départ d'un pair, changement de piste vidéo et (re)connexion. Regroupé sur un
   * tick : plusieurs événements simultanés (arrivée de plusieurs pairs) = un seul recalcul.
   */
  private scheduleSendBudget(): void {
    if (this.budgetTimer) return;
    this.budgetTimer = setTimeout(() => {
      this.budgetTimer = null;
      this.applySendBudget();
    }, 0);
  }

  private applySendBudget(): void {
    const budget = computeSendBudget(this.peers.size);
    for (const peer of this.peers.values()) {
      const sender = peer.videoSender;
      const track = sender?.track;
      if (!sender || !track) continue;
      const sharing = track === this.screenTrack;
      void this.applyEncoding(
        sender,
        sharing ? budget.screenBps : budget.cameraBps,
        // La netteté du texte prime sur la charge CPU : jamais de réduction pour l'écran.
        sharing ? 1 : budget.cameraScaleDown,
      );
    }
  }

  private async applyEncoding(
    sender: RTCRtpSender,
    maxBitrateBps: number,
    scaleResolutionDownBy: number,
  ): Promise<void> {
    try {
      const params = sender.getParameters();
      const encoding = params.encodings?.[0];
      if (encoding) {
        encoding.maxBitrate = maxBitrateBps;
        encoding.scaleResolutionDownBy = scaleResolutionDownBy;
      } else {
        params.encodings = [{ maxBitrate: maxBitrateBps, scaleResolutionDownBy }];
      }
      await sender.setParameters(params);
    } catch (err) {
      console.warn('[webrtc] setParameters(maxBitrate) failed', err);
    }
  }

  async startScreenShare(): Promise<boolean> {
    if (this.screenTrack || !this.localStream) return false;

    let displayStream: MediaStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: SCREEN_SHARE_VIDEO_CONSTRAINTS,
        audio: false,
      });
    } catch (err) {
      console.warn('[webrtc] getDisplayMedia refusé/annulé/non supporté', err);
      return false;
    }
    const screenTrack = displayStream.getVideoTracks()[0];
    if (!screenTrack) return false;

    this.screenTrack = screenTrack;
    await this.replaceOnAllPeers('video', screenTrack);

    // Mute le flux affiché LOCALEMENT en mutant le même objet MediaStream déjà
    // lié aux <video> (room-page.tsx/VideoTile) — un élément vidéo lié réagit
    // nativement à addtrack/removetrack sur le même flux, aucun changement
    // requis côté composants.
    const cameraTrack = this.cameraTrack;
    if (cameraTrack && this.localStream.getVideoTracks().includes(cameraTrack)) {
      this.localStream.removeTrack(cameraTrack);
    }
    this.localStream.addTrack(screenTrack);

    // Revient automatiquement à la caméra si l'utilisateur clique "Stop
    // sharing" dans l'UI native du navigateur (hors de notre bouton).
    screenTrack.onended = () => this.stopScreenShare();

    this.lastSentScreenSharing = true;
    this.broadcastScreenShareState(true);
    this.emitScreenShareChanged(true);
    return true;
  }

  stopScreenShare(): void {
    if (!this.screenTrack || !this.localStream) return;
    const screenTrack = this.screenTrack;
    // Retour à la caméra si elle est allumée, sinon à « aucune vidéo » (participant sans caméra).
    const cameraTrack = this.isLive(this.cameraTrack) ? this.cameraTrack : null;

    this.screenTrack = null;
    void this.replaceOnAllPeers('video', cameraTrack);

    this.localStream.removeTrack(screenTrack);
    if (cameraTrack) this.localStream.addTrack(cameraTrack);
    screenTrack.stop();

    this.lastSentScreenSharing = false;
    this.broadcastScreenShareState(false);
    this.emitScreenShareChanged(false);
  }

  onScreenShareChanged(cb: (sharing: boolean) => void): () => void {
    this.screenShareListeners.add(cb);
    return () => this.screenShareListeners.delete(cb);
  }

  private emitScreenShareChanged(sharing: boolean): void {
    for (const cb of this.screenShareListeners) cb(sharing);
  }

  private broadcastScreenShareState(sharing: boolean): void {
    for (const peerId of this.peers.keys()) {
      this.sendSignal({
        kind: 'screen-share-state',
        from: this.me.userId,
        to: peerId,
        data: { sharing },
      });
    }
  }

  sendTranscriptionState(active: boolean): void {
    this.lastSentTranscriptionActive = active;
    for (const peerId of this.peers.keys()) {
      this.sendSignal({
        kind: 'transcription-state',
        from: this.me.userId,
        to: peerId,
        data: { active },
      });
    }
  }

  onTranscriptionStateChanged(cb: (active: boolean) => void): () => void {
    this.transcriptionListeners.add(cb);
    return () => this.transcriptionListeners.delete(cb);
  }

  async leaveRoom(): Promise<void> {
    if (this.budgetTimer) {
      clearTimeout(this.budgetTimer);
      this.budgetTimer = null;
    }
    // Arrête TOUTES les pistes locales : la caméra mise « en réserve » pendant un partage
    // d'écran n'est plus dans `localStream`, le voyant resterait sinon allumé.
    for (const track of [this.screenTrack, this.cameraTrack, this.micTrack]) track?.stop();
    this.screenTrack = null;
    this.cameraTrack = null;
    this.micTrack = null;
    this.localStream = null;
    for (const peer of this.peers.values()) this.closePeer(peer);
    this.peers.clear();
    if (this.channel) {
      await this.channel.untrack();
      await this.channel.unsubscribe();
      this.channel = null;
    }
    this.emitConnectionState('ended');
  }

  /**
   * TERMINE réellement la réunion (hôte) — `end_visio_room` : heure de fin =
   * maintenant, rendez-vous « passé », salle qui ne se rouvre plus, POUR TOUS. La fin est
   * enregistrée AVANT le reste : si elle échoue on lève l'erreur SANS quitter ni prévenir
   * personne (sinon l'hôte croirait avoir terminé une réunion restée ouverte). Une fois
   * enregistrée : `room-ended` diffusé (chaque récepteur le vérifie côté serveur) + un
   * `bye` par pair pour les ANCIENS clients (qui ne connaissent pas `room-ended` ; à retirer
   * quand toutes les apps sont à jour), puis départ.
   * Pour sortir SANS terminer (« je reviens »), c'est `leaveRoom()`.
   */
  async endRoom(): Promise<void> {
    if (this.roomId) {
      const { error } = await this.db.rpc('end_visio_room', { p_room_id: this.roomId });
      if (error) {
        console.error('[webrtc] end_visio_room failed', error);
        throw error;
      }
    }
    this.sendSignal({ kind: 'room-ended', from: this.me.userId });
    for (const peerId of this.peers.keys()) {
      this.sendSignal({ kind: 'bye', from: this.me.userId, to: peerId });
    }
    await this.leaveRoom();
  }
}
